import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import API_BASE_URL from "../config/api";
import { Tooltip } from "bootstrap";
import { color } from "framer-motion";

function Chapterwise() {
  const { subjectId } = useParams();
  const navigate = useNavigate();

  const [activeId, setActiveId] = useState(null);
  const [subjectName, setSubjectName] = useState("");
  const [ChapterCount, setChapterCount] = useState("");
  const [chapters, setChapters] = useState([]);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const toTitleCase = (str) => str?.toUpperCase();

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem("user"));
    const userId = user?.id;

    if (!userId) {
      Swal.fire(
        "Not Logged In",
        "Please login to view the dashboard",
        "warning"
      ).then(() => {
        window.location.href = "/login";
      });
      return;
    }

    const fetchChapters = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/get_subject_chapters_new.php?subject_id=${subjectId}&user_id=${userId}`
        );

        const data = await res.json();

        if (data.success) {
          setSubjectName(data.subject?.name || "");
          setChapterCount(data.subject?.chapter_count || "");

          const chapterList = data.chapters || [];
          setChapters(chapterList);

          setTotalTime(data.total_time ?? 30);

          const planId = data.plan_id ?? 0;
          setHasSubscription(planId > 0);

          // ✅ Automatically select the first chapter on page load
          if (chapterList.length > 0) {
            const firstChapter = chapterList[0];

            setActiveId(firstChapter.id);

            // Show first chapter total quiz
            setTotalQuestions(Number(firstChapter.total_quiz) || 0);
          } else {
            setActiveId(null);
            setTotalQuestions(0);
          }
        } else {
          Swal.fire(
            "Error",
            data.message || "Failed to fetch chapters from API",
            "error"
          );
        }
      } catch (error) {
        console.error(error);

        Swal.fire(
          "Error",
          "Failed to fetch chapters from API",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchChapters();
  }, [subjectId]);

  // ✅ Initialize Bootstrap Tooltip
  useEffect(() => {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    tooltipTriggerList.forEach((el) => {
      new Tooltip(el);
    });
  }, [chapters]);

  const handleChapterClick = (chapter) => {
    if (!hasSubscription) {
      Swal.fire({
        title: "Subscription Required",
        text: "Please activate your subscription to unlock this chapter.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "View Plans",
        cancelButtonText: "Close",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/subscription");
        }
      });
      return;
    }

    if (chapter.locked) {
      Swal.fire({
        title: "Chapter Locked",
        text: "This chapter is locked. Please upgrade your plan or check your attempts.",
        icon: "warning",
        confirmButtonText: "Close",
      });
      return;
    }
    // console.log("Selected Chapter:", chapter);
    setTotalQuestions(Number(chapter.total_quiz) || 0);
    setActiveId(chapter.id);
  };

  const handleStartExam = (e) => {

    if (!activeId) {
      e.preventDefault();

      Swal.fire({
        icon: "warning",
        title: "No Chapter Available",
        text: "No chapter found for this subject."
      });

      return;
    }

    if (!hasSubscription) {
      e.preventDefault();

      Swal.fire({
        title: "Subscription Required",
        text: "You cannot start any exam without a subscription.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "View Plans",
        cancelButtonText: "Close",
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/subscription");
        }
      });

      return;
    }
  };

  if (loading) {
    return <div className="container my-5">Loading chapters...</div>;
  }

  return (
    <section>
      <div className="container my-5">
        {/* Heading */}
        <div className="row mb-4 align-to-left">
          <div className="row">
            <div className="col-xl-12 col-lg-12 col-md-12">
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item text-muted"><Link to="/studentdashboard">Dashboard</Link></li>
                  <li className="breadcrumb-item text-muted"><Link to="/subjects">Subject</Link></li>
                  <li className="breadcrumb-item"><Link to="#" className="text-main">{subjectName}</Link></li>
                </ol>
              </nav>
            </div>
          </div>
          <div className="col">
            <h3 className="fw-bold mb-1">{subjectName}</h3>
            <div style={{ color: "rgb(22 77 133)", fontWeight: "500" }}>
              {/* Total Chapters: {ChapterCount}  */}
            </div>
          </div>
        </div>

        <div className="row">
          {/* Left Side */}
          <div className="col-md-4">
            <div style={{ maxHeight: "450px", overflowY: "auto", paddingRight: "4px" }}>
              {chapters.map((chapter) => {
                const isActive = activeId === chapter.id;

                const attempts =
                  chapter.attempts ??
                  chapter.attempt_count ??
                  chapter.total_attempts ??
                  0;

                const showLock = !hasSubscription || chapter.locked;

                return (
                  <div
                    key={chapter.id}
                    onClick={() => handleChapterClick(chapter)}
                    className="d-flex justify-content-between align-items-center border mb-2 px-3 py-3"
                    style={{
                      cursor: "pointer",
                      backgroundColor: isActive ? "rgb(22 77 133)" : "#ffffff",
                      color: isActive ? "#ffffff" : "#000000",
                      borderRadius: "6px",
                    }}
                  >
                    <div className="d-flex flex-column align-items-start">
                      <span className="fw-semibold" style={{
                        color: isActive ? "#ffffff" : "rgb(234 127 16)",
                      }}>
                        Chapter {chapter.description}
                      </span>

                      <span
                        style={{
                          color: isActive ? "#ffffff" : "rgb(22, 77, 133)",
                        }}
                      >
                        {toTitleCase(chapter.title)}
                      </span>
                    </div>

                    {showLock ? (
                      <span style={{ fontSize: "22px" }}>🔒</span>
                    ) : (
                      <span
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "35px",
                          height: "35px",
                          fontWeight: "bold",
                          backgroundColor: "rgb(236 148 55)",

                        }}
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title={`You have attempted this chapter ${attempts} times`}
                      >
                        {attempts}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Side */}
          <div className="col-md-8">
            <div className="d-flex justify-content-between mb-4">
              <h4 className="fw-bold align-to-left" style={{ color: "#164d85" }}>
                Total Questions : {totalQuestions}
              </h4>
            </div>

            <div className="mb-4 align-to-left">
              <h4>Instruction :</h4>
              <ul className="simple-list listcolor">

                <li>For each attempt, {totalQuestions < 100 ? totalQuestions : "100"} questions will be randomly selected from the question bank.</li>
                <li>Multiple attempts are permitted.</li>
                <li>Do not refresh or close your browser during the quiz.</li>
                <li>Ensure a stable internet connection.</li>
                <li>
                  Click <strong style={{ color: "green" }}>Submit</strong> to save your answers.
                </li>
              </ul>
            </div>

            <div className="d-flex justify-content-end">
              <Link
                to={activeId ? `/mocktest/chapterquiz/${activeId}` : "#"}
                onClick={handleStartExam}
              >
                <button
                  className="btn btn-primary btn-main px-4"
                  disabled={!activeId || !hasSubscription}
                  style={{
                    opacity: activeId && hasSubscription ? 1 : 0.5,
                    cursor: activeId && hasSubscription ? "pointer" : "not-allowed",
                  }}
                >
                  Start
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Chapterwise;